import React, { useState, useContext, useEffect } from "react";

// External
import { useFormikContext } from "formik";
import _ from "lodash";

// Material-UI
import { Grid, Badge, makeStyles } from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import NearMeIcon from "@material-ui/icons/NearMe";
import SyncIcon from "@material-ui/icons/Sync";
import theme from "@Styles/theme";
import TextField from "@material-ui/core/TextField";

// Internal
import AdminDevice from "sccAdminDevice";
import Client from "sccClient";
import Language from "sccLanguage";
import Utils from "sccUtils";
import Sync from "sccSync";
import SyncMenu from "sccSyncMenu";
import TimeUtils from "sccTimeUtils";
import Geofence from "sccGeofence";
import GeofenceOverlay from "sccGeofenceOverlay";
import Device from "sccDevice";
import Alert from "sccAlert";
import Feed from "sccFeed";
import Permission from "sccPermission";
import Profile from "sccProfile";
import Poi from "sccPoi";
import CustomDialog from "@Components/CustomDialog/index.js";
import CustomPagination from "@Components/CustomPagination";
import CustomDialogYesNoCancel from "../../CustomDialogYesNoCancel";
import CustomButton from "@Components/CustomButton";
import CustomTooltip from "@Components/CustomTooltip";
import { ModuleWrapperContext } from "../ModuleWrapperContext";
import { SelectedGeofenceContext } from "@Modules/geofence/components/context/SelectedGeofenceContext";
import CustomDialogYesNo from "@Components/CustomDialogYesNo/CustomDialogYesNo";
import { AppContext } from "../../../AppContext";
import MasterDataFile from "@Modules/device/body/MasterDataFile";
import { FederationDataContext } from "@Modules/federation_data/context/FederationDataContext";
import { LdapContext } from "@Modules/ldap/context/ldapContext";
import UserSetting from "sccUserSetting";

const MW_LIST_MODE = process.env.REACT_APP_MW_LIST_MODE;
const MW_ADDEDIT_MODE = process.env.REACT_APP_MW_ADDEDIT_MODE;
const MW_SINGLEPAGE_MODE = process.env.REACT_APP_MW_SINGLEPAGE_MODE;
const MW_ALERTS_MODULE_MODE = process.env.REACT_APP_MW_ALERTS_MODULE_MODE;
const MW_ASSETS_MODULE_MODE = process.env.REACT_APP_MW_ASSETS_MODULE_MODE;
const MW_MESSAGE_MODULE_MODE = process.env.REACT_APP_MW_MESSAGE_MODULE_MODE;
const MW_MESSAGE_MODULE_LIST_MODE =
  process.env.REACT_APP_MW_MESSAGE_MODULE_LIST_MODE;
const MW_MESSAGE_MODULE_COMPOSE_MODE =
  process.env.REACT_APP_MW_MESSAGE_MODULE_COMPOSE_MODE;
const MW_IRIDIUM_MODULE_MODE = process.env.REACT_APP_MW_IRIDIUM_MODULE_MODE;
const MW_SYNC_MODULE_MODE = process.env.REACT_APP_MW_SYNC_MODULE_MODE;

const useStyles = makeStyles((theme) => ({
  footer: {
    padding: "5px",
    backgroundColor: "#222b30",
    width: "100%",
    color: "#5b7182",
  },

  actionButton: {
    padding: 0,
  },

  syncActionButton: {
    padding: 0,
    flexWrap: "nowrap",
  },

  gapSpan: {
    width: 8,
    display: "inline-flex",
  },

  syncFooterItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.colors.green.main,
    borderColor: "#4cae4c",
    color: "#293239",
    margin: "0px 2px",
    height: 32,
    paddingRight: "11px",

    "& span": {
      fontSize: 11,
    },
  },

  pendingFooterItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.colors.orange.main,
    borderColor: theme.palette.colors.orange.main,
    color: "#293239",
    margin: "0px 2px",
    height: 32,

    "& span": {
      paddingRight: 6,
      fontSize: 11,
    },
  },

  syncingFooterItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.colors.blue.main,
    borderColor: "#2e6da4",
    color: "#293239",
    margin: "0px 2px",
    position: "relative",
    height: 32,
  },

  syncFooterText: {
    paddingRight: 12,
    fontSize: 11,
    display: "inherit",
    maxWidth: 50,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  syncBadge: {
    "& span": {
      backgroundColor: "#293239",
      color: theme.palette.colors.green.main,
    },
  },

  pendingBadge: {
    "& span": {
      backgroundColor: "#293239",
      color: theme.palette.colors.orange.main,
    },
  },

  syncingBadge: {
    "& span": {
      backgroundColor: "#293239",
      color: theme.palette.colors.blue.main,
    },
  },

  sycnIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    left: 6,
    paddingRight: "0px !important",

    "& svg": {
      fontSize: 12,
    },
  },
}));

function ModuleWrapperFooter(props) {
  const [btnAddText] = useState(Language.translate("Add"));
  const [btnSaveText] = useState(Language.translate("Save"));
  const [btnSaveAndAddText] = useState("Save & Add");
  const [btnCancelText] = useState(Language.translate("Cancel"));
  const [btnDeleteText] = useState(Language.translate("Delete"));
  const { filterSelection, filteredList } = props;
  const [fedState, setFedState] = useContext(FederationDataContext);

  const acknowledgeAllText = Language.translate(
    "Are you sure you want to acknowledge all"
  );

  const [alertsConfirmationOpen, setAlertsConfirmationOpen] = useState(false);

  const [mwState, setMwState] = useContext(ModuleWrapperContext);
  const [appState] = useContext(AppContext);
  const { setModuleEditMode } = useContext(SelectedGeofenceContext);
  const { isLdapLoggedIn, ldapSync, isLdapSyncing } = useContext(LdapContext);

  const updateModuleData = mwState.updateModuleData;
  const handleItemDelete = mwState.handleItemDelete;
  const changeToComposeMode = mwState.changeToComposeMode;

  const originalValues = mwState.moduleItemDataOriginal;
  const [password, setPassword] = useState();
  // these state and function are used to display confirmation box in address book to prompt if sync address_book
  const [openYesNoCancelDialogABsync, setOpenYesNoCancelDialogABsync] =
    useState(false);

  const [
    openYesNoDialogClientFeaturesRemoval,
    setOpenYesNoDialogClientFeaturesRemoval,
  ] = useState(false);
  const [confirmFeaturesRemovalMsg, setConfirmFeaturesRemovalMsg] =
    useState("");

  const [removeABdataFromClient, setRemoveABdataFromClient] = useState(false);
  const [removeSAdataFromClient, setRemoveSAdataFromClient] = useState(false);
  const [removeHermesDataFromClient, setRemoveHermesDataFromClient] =
    useState(false);

  const classes = useStyles();

  const [openEnterPasswordDialog, setOpenEnterPasswordDialog] = useState(false);
  const [showDataChangeWarning, setShowDataChangeWarning] = useState(false);
  const [dataHasChange, setDataHasChange] = useState(false);
  const [matchIds, setMatchIds] = useState(false);

  const formik = useFormikContext();

  const moduleItemData = formik.values;

  // replace max_speed or min_speed value to pass hasChanged validation
  if (moduleItemData.max_speed === "") {
    moduleItemData.max_speed = null;
  }
  if (moduleItemData.min_speed === "") {
    moduleItemData.min_speed = null;
  }

  const handleDialogOpenClose = () => {
    mwState.setDialogOkAction(() => okActionClose());
    mwState.setDialogCancelAction(() => cancelAction());
    mwState.setDialogOpen(true);
  };
  const handleDialogOpen = () => {
    mwState.setDialogOkAction(() => okAction());
    mwState.setDialogCancelAction(() => cancelAction());
    mwState.setDialogOpen(true);
  };
  const handleDialogOpenMessage = () => {
    mwState.setDialogOkAction(() => okActionMessage());
    mwState.setDialogCancelAction(() => cancelAction());
    mwState.setDialogOpen(true);
  };

  // Admin Device Module and Bulk Add Assets
  function adminDeviceSaveAndAdd() {
    let continueFurther = true;
    if (formik.values.title) {
      formik.values.title = formik.values.title.trim();
    }
    const formikValues = formik.values;
    //  backend does not accept empty string, here we replace "" to null to pass validation
    if (formikValues.max_speed === "") {
      formikValues.max_speed = null;
    }

    if (formikValues.min_speed === "") {
      formikValues.min_speed = null;
    }

    if (typeof mwState.handleSpecialSaveValidation === "function") {
      continueFurther = mwState.handleSpecialSaveValidation();
    }

    if (!continueFurther) {
      setMwState((p) => ({
        ...p,
        isSaveInProgress: false,
      }));
      return false;
    }

    formik.validateForm().then((validation) => {
      formik.setTouched(validation);
      if (Object.keys(validation).length === 0) {
        // Form is valid, do any success call
        const message = {
          title: mwState.title,
          text: Language.translate(
            "Successfully " + (formik.values.id ? "Updated" : "Created")
          ),
          subText: formik.values[mwState.searchField[0]]
            ? formik.values[mwState.searchField[0]]
            : null,
          backToWrapperDisplayMode: MW_ADDEDIT_MODE, //updated this to bring the user back to edit mode after save
        };

        // if we change type from Hawyeke to other types, we don't need to pass vehicle value
        if (formikValues.vehicle && formikValues.type !== "Hawkeye 5500") {
          delete formikValues["vehicle"];
        }

        // correct feedCode when update admin device
        if (formikValues.feed_codes.length > 0) {
          const allFeeds = Object.keys(Feed.get()).map((item) => Number(item));
          let newFeedCodes = formikValues.feed_codes;
          formikValues.feed_codes = _.intersection(allFeeds, newFeedCodes);
        }

        const newValues = _.cloneDeep(formik.values);
        updateModuleData(formikValues, mwState.routeUrl, message).then(
          (data) => {
            // refresh list
            if (data && data?.result?.id) {
              let filteredData = mwState.filteredModuleData;
              const moduleData = mwState.moduleData;
              const updatedItem = data.result;

              moduleData[updatedItem.id] = updatedItem;

              // check if there are filters
              if (!_.isEmpty(filterSelection)) {
                filteredData = mwState.arrayToObject(
                  filteredList(Object.values(moduleData), filterSelection),
                  "id"
                );
              } else {
                filteredData[updatedItem.id] = updatedItem;
              }
              newValues.name = getNewNameValue(newValues.name);
              newValues.id = null;
              newValues.imei = "";
              newValues.Serial_No = "";
              newValues.sms = "";
              newValues.device_configurations.zigBee_id = null;
              setMwState((p) => ({
                ...p,
                moduleData: moduleData,
                filteredModuleData: filteredData,
                isSaveInProgress: false,
                moduleItemData: {
                  ...newValues,
                  type: newValues.type_id
                    ? AdminDevice.getDeviceType(newValues.type_id).title
                    : null,
                },
                moduleItemDataOriginal: {
                  ...newValues,
                  type: newValues.type_id
                    ? AdminDevice.getDeviceType(newValues.type_id).title
                    : null,
                },
              }));
            }
          }
        );
      } else {
        setMwState((p) => ({
          ...p,
          isSaveInProgress: false,
        }));
      }
    });
  }

  const regExCheck = /\d+$/;
  function getNewNameValue(nameValue) {
    let newName;
    let nameString;
    let leadZeroes;
    let formatName;
    // if name value has end digits, increments name value by 1, else appends "2"
    if (regExCheck.test(nameValue)) {
      newName = nameValue.replace(regExCheck, function (n) {
        return ++n;
      });

      if (newName.length < nameValue.length) {
        nameString = nameValue.split(regExCheck)[0];
        leadZeroes = nameValue
          .match(regExCheck)
          .toString()
          .match(/^0+/)
          .toString();
        formatName =
          nameString + leadZeroes + newName.match(regExCheck).toString();

        if (formatName.length > nameValue.length) {
          let removeZero = leadZeroes.slice(0, -1);
          formatName =
            nameString + removeZero + newName.match(regExCheck).toString();
        }

        newName = formatName;
      }
    } else {
      newName = nameValue;
      newName += "2";
    }
    return newName;
  }

  function changeToAddModeFromAssets(e, subMode) {
    e.preventDefault();
    setMwState((p) => ({
      ...p,
      wrapperDisplayMode: MW_ADDEDIT_MODE,
      wrapperDisplaySubMode: subMode,
    }));
  }

  function changeToAddMode(e) {
    e.preventDefault();
    setMwState((p) => ({
      ...p,
      wrapperDisplayMode: MW_ADDEDIT_MODE,
    }));
  }

  function okActionClose() {
    mwState.closeModuleWrapperPopper();
    mwState.setDialogOpen(false);
  }
  function okAction() {
    cancelAndBackToListingFunction();
    mwState.setDialogOpen(false);
  }
  function okActionMessage() {
    mwState.cancelAndBackToMessageListingMode();
    mwState.setDialogOpen(false);
  }
  function cancelAction() {
    mwState.setDialogOpen(false);
  }
  function cancelAndBackToListingFunction() {
    setMwState((p) => ({
      ...p,
      moduleFormHasChanges: false,
    }));
    if (mwState.moduleName === "Geofence") {
      // set map background to light
      setModuleEditMode("end");
      window.olMap.editMode = "end";
      GeofenceOverlay.endAllModifications();
      // make sure GeofenceOverlay returns to original setting
      GeofenceOverlay.setBlockSelection(false);
      GeofenceOverlay.deselectFeature();
      // reset Geofence
      const geofenceId = mwState.moduleItemData.id;
      if (geofenceId) {
        const geofence = Geofence.get(geofenceId);
        GeofenceOverlay.refresh(geofence);
      } else {
        // if in Add mode, end drawing
        GeofenceOverlay.endDrawing();
      }
      GeofenceOverlay.resetFeatureStyleToPreEdit();
      GeofenceOverlay.floatingFeatureOnMap = false;
      GeofenceOverlay.selectIconSource.clear();
      GeofenceOverlay.setDrawFeature(null);
    }
    if (mwState.moduleName !== "Device") {
      mwState.cancelAndBackToListingMode();
    } else {
      mwState.cancelAndBackToAssetListingMode();
    }
  }

  function customerSaveAndBack() {
    const newFeatureList = moduleItemData.features;
    const originalFeatureList = originalValues.features;
    var authRequiredFeatureBeingRemoved = false;
    var featuresToRemove = _.filter(originalFeatureList, (x) => {
      if (!newFeatureList.includes(x)) {
        if ([6, 11, 12].includes(x)) {
          authRequiredFeatureBeingRemoved = true;
        }
      }
      return !newFeatureList.includes(x);
    });

    if (featuresToRemove.length && authRequiredFeatureBeingRemoved) {
      _.each(featuresToRemove, (featureId) => {
        switch (featureId) {
          case 6:
            setRemoveSAdataFromClient(true);
            break;
          case 11:
            setRemoveABdataFromClient(true);
            break;
          case 12:
            setRemoveHermesDataFromClient(true);
        }
      });
    } else {
      saveAndBackToList();
    }
  }

  useEffect(() => {
    if (
      removeSAdataFromClient ||
      removeABdataFromClient ||
      removeHermesDataFromClient
    ) {
      showAuthRequiredFeaturesRemovalDialog();
    }
  }, [
    removeSAdataFromClient,
    removeABdataFromClient,
    removeHermesDataFromClient,
  ]);

  function showAuthRequiredFeaturesRemovalDialog() {
    const confirmBoxMsg1 =
      Language.translate(
        "The Following feature(s) will be removed from this customer:"
      ) + " ";

    var changedModulesString = "";
    if (removeHermesDataFromClient) {
      changedModulesString =
        changedModulesString + "- Hermes (Gateway Devices)";
    }
    if (removeABdataFromClient) {
      changedModulesString =
        changedModulesString + "- " + "Address Book (Address books)";
    }
    if (removeSAdataFromClient) {
      changedModulesString =
        changedModulesString + "- " + "Situational Awareness (Rules)";
    }

    changedModulesString =
      changedModulesString +
      " " +
      Language.translate(
        "Password confirmation will be required to complete this action."
      ) +
      " " +
      Language.translate("Press YES to continue.");

    setConfirmFeaturesRemovalMsg(confirmBoxMsg1 + changedModulesString);
    setOpenYesNoDialogClientFeaturesRemoval(true);
  }

  function removeFeaturesDataFromClientAndSave() {
    //Menu.prototype.save.call($this);
    if (removeABdataFromClient) {
      Client.deleteAllAddressBooks(moduleItemData.id);
    }
    if (removeHermesDataFromClient) {
      Client.deleteAllHermes(moduleItemData.id);
    }
    if (removeSAdataFromClient) {
      Client.deleteAllSaRules(moduleItemData.id);
    }
    setOpenYesNoDialogClientFeaturesRemoval(false);
    resetAuthRequiredFeaturesCheckStates();
    saveAndBackToList();
  }

  function resetAuthRequiredFeaturesCheckStates() {
    Client.emptyAbFeature();
    Client.emptyHermesFeature();
    Client.emptySaFeature();

    setRemoveSAdataFromClient(false);
    setRemoveABdataFromClient(false);
    setRemoveHermesDataFromClient(false);
  }

  function saveAndBackToList() {
    let continueFurther = true;
    if (formik.values.title) {
      formik.values.title = formik.values.title.trim();
    }
    const formikValues = formik.values;

    if (mwState.moduleName === "Geofence") {
      // set map background to light
      setModuleEditMode("end");
      window.olMap.editMode = "end";
      // make sure GeofenceOverlay returns to original setting
      GeofenceOverlay.endAllModifications();
      GeofenceOverlay.setBlockSelection(false);
      GeofenceOverlay.deselectFeature();
      GeofenceOverlay.removeDashStrokeFromFeature();
      GeofenceOverlay.floatingFeatureOnMap = false;
      GeofenceOverlay.selectIconSource.clear();
      GeofenceOverlay.setDrawFeature(null);
      GeofenceOverlay.refresh();

      // this is to fix polygon sent from tactical (which has no end point coordinate)
      // todo: it is better to change NTAC code to set width = 1 and same start / end coordinates under polygon
      if (formikValues.shape === "polygon" && !formikValues.width) {
        formikValues.width = 1;
      }
    }

    if (mwState.moduleName === "SA") {
      // convert distance to meters before save
      let unit = UserSetting.get("speed_format");

      if (unit === "MPH" || unit === "KNOTS") {
        formikValues.distance = Math.round(
          Utils.convertUnit(formikValues.distance, "FT", "M")
        );
      }
    }

    //  backend does not accept empty string, here we replace "" to null to pass validation
    if (formikValues.max_speed === "") {
      formikValues.max_speed = null;
    }

    if (formikValues.min_speed === "") {
      formikValues.min_speed = null;
    }

    if (typeof mwState.handleSpecialSaveValidation === "function") {
      continueFurther = mwState.handleSpecialSaveValidation();
    }

    if (!continueFurther) {
      setMwState((p) => ({
        ...p,
        isSaveInProgress: false,
      }));
      return false;
    }

    formik.validateForm().then((validation) => {
      formik.setTouched(validation);
      if (Object.keys(validation).length === 0) {
        // Form is valid, do any success call
        const message = {
          title: mwState.title,
          text: Language.translate(
            "Successfully " + (formik.values.id ? "Updated" : "Created")
          ),
          subText: formik.values[mwState.searchField[0]]
            ? formik.values[mwState.searchField[0]]
            : null,
          //special fix for the Device module
          backToWrapperDisplayMode:
            mwState.moduleName === "Device"
              ? MW_ASSETS_MODULE_MODE
              : MW_LIST_MODE,
        };

        // if we change type from Hawyeke to other types, we don't need to pass vehicle value
        if (
          mwState.moduleName === "Admin Device" &&
          formikValues.vehicle &&
          formikValues.type !== "Hawkeye 5500"
        ) {
          delete formikValues["vehicle"];
        }

        // correct feedCode when update admin device
        if (
          mwState.moduleName === "Admin Device" &&
          formikValues.feed_codes.length > 0
        ) {
          const allFeeds = Object.keys(Feed.get()).map((item) => Number(item));
          let newFeedCodes = formikValues.feed_codes;
          formikValues.feed_codes = _.intersection(allFeeds, newFeedCodes);
        }

        updateModuleData(formikValues, mwState.routeUrl, message).then(
          (data) => {
            // for SA module, update subscriber devices if there is diffrence
            if (mwState.moduleName === "SA") {
              const originalSubscribers =
                mwState.moduleItemDataOriginal.subscribers.devices;
              const currentSubscribers = formikValues.subscribers.devices;

              let removedSubscribers = [];

              if (originalSubscribers.length > 0) {
                _.each(originalSubscribers, (subscriber) => {
                  if (!currentSubscribers.includes(subscriber)) {
                    removedSubscribers.push(subscriber);
                  }
                });
              }

              if (removedSubscribers.length > 0) {
                _.each(removedSubscribers, (removed) => {
                  let device = Device.get(removed);
                  device.latest_sa_timestamp = 0; // set last_sa_send to 0 and now display it on wave device popup
                  var options;
                  options = {
                    url: Utils.apiUrlPrefix + "/device",
                    body: device,
                    method: "PUT",
                  };

                  Utils.httpRequestHandler(options).then(() => {
                    return Promise.resolve();
                  });
                });
              }
            }

            if (
              mwState.moduleName === "Federation" &&
              fedState.federationConnection?.federationServerId ===
                formikValues.id
            ) {
              Utils.disconnectFederationServer(
                formikValues.id,
                formikValues.name
              ).then((res) => {
                setFedState((p) => ({
                  ...p,
                  federationConnection: {},
                }));
              });
            }
            //We do not want the moduledata to be updated like this after an update or save
            if (mwState.moduleName !== "Device") {
              // refresh list
              if (data && data?.result?.id) {
                let filteredData = mwState.filteredModuleData;
                const moduleData = mwState.moduleData;
                const updatedItem = data.result;

                // update Roles groupedPermissions
                if (mwState.moduleName === "Permission") {
                  updatedItem.groupedPermissions = updatedItem.permissions
                    ? Permission.getGroupedPermissions(updatedItem.permissions)
                    : null;
                }

                moduleData[updatedItem.id] = updatedItem;

                // check if there are filters
                if (!_.isEmpty(filterSelection)) {
                  filteredData = mwState.arrayToObject(
                    filteredList(Object.values(moduleData), filterSelection),
                    "id"
                  );
                } else {
                  filteredData[updatedItem.id] = updatedItem;
                }

                setTimeout(() => {
                  let moduleLocalData = moduleData;
                  switch (mwState.moduleName) {
                    case "Poi":
                      moduleLocalData = Poi.localServerData();
                      break;
                    case "Geofence":
                      moduleLocalData = Geofence.localServerData();
                      break;
                  }
                  setMwState((p) => ({
                    ...p,
                    moduleLocalData,
                    moduleData: moduleData,
                    filteredModuleData: filteredData,
                    isSaveInProgress: false,
                  }));
                }, 100);
              }
            }
          }
        );
      } else {
        setMwState((p) => ({
          ...p,
          isSaveInProgress: false,
        }));
      }
    });
  }

  // these state and function are used to display confirmation box in address book to prompt if sync address_book
  const [openYesNoCancelDialog, setOpenYesNoCancelDialog] = useState(false);

  function hasSubscribers(ab) {
    return (
      ab.subscribers.groups.length > 0 || ab.subscribers.devices.length > 0
    );
  }

  function hasMembers(ab) {
    return (
      ab.members.groups.length > 0 ||
      ab.members.devices.length > 0 ||
      ab.members.users.length > 0
    );
  }

  function addressBookSaveAndBack() {
    if (formik.values.title) {
      formik.values.title = formik.values.title.trim();
    }
    const newValues = formik.values;
    const originalValues = mwState.moduleItemDataOriginal;

    const checkSubscribers = _.isEqual(
      newValues.subscribers,
      originalValues.subscribers
    );
    const checkMembers = _.isEqual(newValues.members, originalValues.members);

    if (!Permission.isModuleAvailable("sync")) {
      saveAddressBook();
    } else if (!hasSubscribers(newValues) && checkSubscribers) {
      //Has no subs and subs have not changed
      saveAddressBook();
    } else if (checkMembers && checkSubscribers) {
      //Subscribers and members have not changed
      saveAddressBook();
    } else if (
      !hasSubscribers(newValues) &&
      !hasMembers(newValues) &&
      checkSubscribers
    ) {
      //no subs no members AND Subs changed
      saveAddressBook();
    } else if (
      !hasSubscribers(newValues) &&
      hasMembers(newValues) &&
      checkSubscribers
    ) {
      //no subs, has members AND Subs changed
      saveAddressBook();
    } else {
      // open sync confirmation Dialog
      setOpenYesNoCancelDialogABsync(true);
    }
  }

  // For Address_book save use (with or without sync)
  function saveAddressBook(withSync = false) {
    formik.validateForm().then((validation) => {
      formik.setTouched(validation);
      if (Object.keys(validation).length === 0) {
        // Form is valid, do any success call
        const message = {
          title: mwState.title,
          text: Language.translate(
            "Successfully " + (formik.values.id ? "Updated" : "Created")
          ),
          subText: formik.values[mwState.searchField[0]]
            ? formik.values[mwState.searchField[0]]
            : null,
          backToWrapperDisplayMode: MW_LIST_MODE,
        };

        const formikValues = formik.values;

        // check if sync subscribers upon save
        if (withSync) {
          formikValues["sync_now"] = true;
        } else {
          formikValues["sync_now"] = false;
        }
        updateModuleData(formikValues, mwState.routeUrl, message)
          .then((data) => {
            // refresh AB list
            if (data && data?.result?.id) {
              const filteredData = mwState.filteredModuleData;
              const moduleData = mwState.moduleData;
              const updatedItem = data.result;
              moduleData[updatedItem.id] = updatedItem;
              filteredData[updatedItem.id] = updatedItem;
              setMwState((p) => ({
                ...p,
                moduleData: moduleData,
                filteredModuleData: filteredData,
                isSaveInProgress: false,
              }));
            }
          })
          .finally(() => {
            setOpenYesNoCancelDialogABsync(false);
          });
      } else {
        setMwState((p) => ({
          ...p,
          isSaveInProgress: false,
        }));
      }
    });
  }

  function saveAndClose(dontClose = false) {
    let continueFurther = true;
    if (typeof mwState.handleSpecialSaveValidation === "function")
      continueFurther = mwState.handleSpecialSaveValidation();
    if (!continueFurther) {
      setMwState((p) => ({
        ...p,
        isSaveInProgress: false,
      }));
      return false;
    }
    formik.validateForm().then((validation) => {
      formik.setTouched(validation);
      if (Object.keys(validation).length === 0) {
        // Form is valid, do any success call
        const message = {
          title: mwState.title,
          text: Language.translate("Successfully Updated"),
          subText: formik.values[mwState.searchField[0]]
            ? formik.values[mwState.searchField[0]]
            : null,
          closeModule: dontClose ? false : true,
          backToWrapperDisplayMode: MW_SINGLEPAGE_MODE,
        };

        updateModuleData(formik.values, mwState.routeUrl, message).then(
          (data) => {
            if (
              dontClose &&
              mwState.wrapperDisplayMode === MW_SINGLEPAGE_MODE
            ) {
              setMwState((p) => ({
                ...p,
                moduleData: data.result,
                filteredModuleData: data.result,
                moduleItemDataOriginal: data.result,
                moduleItemData: data.result,
                isSaveInProgress: false,
              }));
            } else {
              // refresh list
              if (data && data?.result?.id) {
                const filteredData = mwState.filteredModuleData;
                const moduleData = mwState.moduleData;
                const updatedItem = data.result;

                // update Roles groupedPermissions
                if (mwState.moduleName === "Permission") {
                  updatedItem.groupedPermissions = updatedItem.permissions
                    ? Permission.getGroupedPermissions(updatedItem.permissions)
                    : null;
                }

                moduleData[updatedItem.id] = updatedItem;
                filteredData[updatedItem.id] = updatedItem;

                setMwState((p) => ({
                  ...p,
                  moduleData: moduleData,
                  filteredModuleData: filteredData,
                  isSaveInProgress: false,
                }));
              }
            }
          }
        );
      } else {
        setMwState((p) => ({
          ...p,
          isSaveInProgress: false,
        }));
      }
    });
  }
  function sendMessage() {
    const options = [
      1,
      mwState.pagination.itemsPerPage,
      mwState.moduleData.currentFolder,
      mwState.moduleData.sortDesc,
    ]; //used for reloading
    formik.validateForm().then((validation) => {
      formik.setTouched(validation);
      if (Object.keys(validation).length === 0) {
        //formik values are retrieved to be modified
        let messageValues = _.cloneDeep(formik.values);
        //replace 0 with null incase no canned mesages is selected
        if (formik.values.cannedmessage_id === 0) {
          messageValues.cannedmessage_id = "null";
        }
        // Form is valid, do any success call
        const message = {
          title: "MESSAGE",
          text: Language.translate("Successfully Created"),
          noRefresh: true,
          closeModule: false,
        };
        updateModuleData(messageValues, "/api/v1/message", message);
        mwState.loadMessages(options);
        mwState.backToMessagesList();
      }
    });
  }

  function acknowledgeAll(alertIds) {
    // acknowledge all alerts
    Alert.acknowledge(alertIds)
      .then((response) => {
        if (response.error) {
          appState.displaySnackbarMessage({
            title: mwState.title,
            message: Language.translate(response.error),
            subMessage: "",
            variant: "error",
          });
        } else if (response.result) {
          appState.displaySnackbarMessage({
            title: mwState.title,
            message: Language.translate("Successfully Acknowledged"),
            subMessage: "",
            variant: "success",
          });
        }
      })
      .catch(() => {
        appState.displaySnackbarMessage({
          title: mwState.title,
          message: Language.translate("Error"),
          subMessage: "",
          variant: "error",
        });
      });
  }

  function acknowledgeAlerts(alertIds) {
    acknowledgeAll(alertIds);
    setAlertsConfirmationOpen(false);
  }

  function getAcknowledgeAlertsIds(alerts) {
    let filteredAlerts = {};
    Object.values(alerts).forEach((alert) => {
      if (_.isEmpty(alert.acknowledgements)) {
        filteredAlerts[alert.id] = alert;
      }
    });
    if (filteredAlerts) {
      return Object.keys(filteredAlerts).map((id) => Number(id));
    } else {
      return [];
    }
  }

  function groupClean(bills) {
    return _.map(bills, (bill) => {
      return _.pick(bill, ["imei", "total_bytes", "total_inbox"]);
    });
  }

  function handleExport() {
    if (mwState.moduleItemData.length > 0) {
      Utils.generateCSVFileAndDownload(groupClean(mwState.moduleItemData), {
        file_title:
          "IridiumBilling" + TimeUtils.getCurrentDateTimeAsATag() + ".csv",
      });
    } else {
      appState.displaySnackbarMessage({
        title: "Iridium Billing",
        message: "No data is available.",
        subMessage: null,
        variant: "error",
      });
    }
    return;
  }

  function deleteItem() {
    if (mwState.moduleName === "address_book") {
      handleItemDelete(
        `${mwState.routeUrl}/${moduleItemData.id}/true`,
        mwState.title,
        mwState.moduleName,
        mwState.moduleItemDataOriginal[mwState.searchField[0]]
      );
    } else {
      handleItemDelete(
        `${mwState.routeUrl}/${moduleItemData.id}`,
        mwState.title,
        mwState.moduleName,
        mwState.moduleItemDataOriginal[mwState.searchField[0]],
        moduleItemData.id
      );
      if (
        mwState.moduleName === "Federation" &&
        fedState.federationConnection?.federationServerId === moduleItemData.id
      ) {
        Utils.disconnectFederationServer(
          moduleItemData.id,
          moduleItemData.name
        ).then((res) => {
          setFedState((p) => ({
            ...p,
            federationConnection: {},
          }));
        });
      }
    }
  }
  function onPageChange(currentPage, startIndex, endIndex) {
    //change list display to show the current index of records
    setMwState((p) => ({
      ...p,
      pagination: {
        ...p.pagination,
        currentPage: currentPage,
        startIndex,
        endIndex,
      },
    }));
  }
  function onMessagingPageChange(currentPage, startIndex, endIndex) {
    const currentFolder = mwState.moduleData.currentFolder;
    const sortDesc = _.isBoolean(mwState.moduleData.sortDesc)
      ? mwState.moduleData.sortDesc
      : true;
    if (
      (mwState.pagination.currentPage === currentPage &&
        mwState.moduleData.currentFolder === currentFolder) ||
      currentPage == 0
    ) {
      return false;
    }
    if (!mwState.moduleData.reloading) {
      setMwState((p) => ({
        ...p,
        moduleData: { ...p.moduleData, reloading: true },
        pagination: { ...p.pagination, currentPage },
      }));
      const options = [
        currentPage,
        mwState.pagination.itemsPerPage,
        currentFolder,
        sortDesc,
      ];
      if (
        mwState.searchExecuted &&
        mwState.searchText &&
        mwState.searchText !== ""
      ) {
        options.push(mwState.searchText);
      }
      mwState.loadMessages(options);
    }
  }

  const handlePassword = (e) => {
    setPassword(e.target.value);
  };

  function cancelEnterPasswordDialog() {
    setOpenEnterPasswordDialog(false);
    setOpenYesNoDialogClientFeaturesRemoval(false);
    resetAuthRequiredFeaturesCheckStates();
  }

  const handleLogin = (e) => {
    const username = Profile.get().username;
    const data = { username, password };
    const url = Utils.apiUrlPrefix + "/auth/login";

    return fetch(url, {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.error) {
          appState.displaySnackbarMessage({
            message: Language.translate(
              "Authorization failure. Check your input and ensure you have internet connection."
            ),
            variant: "error",
          });
          setOpenEnterPasswordDialog(false);
          resetAuthRequiredFeaturesCheckStates();
        }
        if (data.token) {
          removeFeaturesDataFromClientAndSave();
          setOpenEnterPasswordDialog(false);
        }
      })
      .catch(function (err) {
        console.log("Error in Catch: ", err);
      });
  };

  if (mwState.showFooter) {
    switch (mwState.wrapperDisplayMode) {
      case MW_LIST_MODE: //generic listing modules
        return (
          <Grid
            justifyContent="space-between"
            className={classes.footer}
            container
          >
            <Grid item className={classes.actionButton} xs={3}>
              {mwState.verifyAddPermission && (
                <CustomButton
                  onClick={changeToAddMode}
                  size="medium"
                  color="command"
                  id="btnAdd"
                  variant="contained"
                  startIcon={<KeyboardArrowRightIcon />}
                >
                  {btnAddText}
                </CustomButton>
              )}
            </Grid>
            <Grid item className={classes.actionButton} xs={3}>
              {/* check for ldap */}
              {mwState.moduleName === "Admin Device" && isLdapLoggedIn && (
                <CustomButton
                  onClick={(e) => {
                    ldapSync();
                  }}
                  size="medium"
                  color={"download"}
                  id="btnAdSync"
                  variant="contained"
                  startIcon={<SyncIcon />}
                >
                  {isLdapSyncing
                    ? Language.translate("Syncing")
                    : Language.translate("AD SYNC")}
                </CustomButton>
              )}
            </Grid>
            {mwState.showPagination && (
              <Grid item align="right" className="pagination" xs={6}>
                <CustomPagination
                  pagination={mwState.pagination}
                  onPageChange={onPageChange}
                />
              </Grid>
            )}
          </Grid>
        );
      case MW_ADDEDIT_MODE: //generic detail pages
        return (
          <Grid
            justifyContent="flex-start"
            className={classes.footer}
            container
          >
            <CustomDialogYesNoCancel
              open={openYesNoCancelDialogABsync}
              // text={Language.translate(
              //   "Would you like to sync this Address Book to the intended Subscribers upon saving?"
              // )}
              //TO BE CHECKED - NOT SURE WHY THE TRANSLATION IS COMMENTED HERE AND PLAIN TEXT IS WRITTEN
              text="Would you like to sync this Address Book to the intended Subscribers upon saving?"
              onNoAction={() => saveAddressBook(false)}
              onYesAction={() => saveAddressBook(true)}
              onCancelAction={() => {
                setMwState((p) => ({
                  ...p,
                  isSaveInProgress: false,
                }));
                setOpenYesNoCancelDialogABsync(false);
              }}
            />
            <CustomDialogYesNo
              open={openYesNoDialogClientFeaturesRemoval}
              text={confirmFeaturesRemovalMsg}
              onNoAction={() => {
                setOpenYesNoDialogClientFeaturesRemoval(false);
                resetAuthRequiredFeaturesCheckStates();
                setRemoveSAdataFromClient(false);
                setRemoveABdataFromClient(false);
                setRemoveHermesDataFromClient(false);
              }}
              onYesAction={() => setOpenEnterPasswordDialog(true)}
            />
            <CustomDialog
              type="logout"
              open={openEnterPasswordDialog}
              onCancelAction={cancelEnterPasswordDialog}
              onOkAction={handleLogin}
              text={
                Language.translate("Please enter your login password") + ":"
              }
              children={
                <TextField
                  autoFocus
                  margin="dense"
                  id="Factory_Reset_Password"
                  type="password"
                  onChange={handlePassword}
                  fullWidth
                />
              }
            />
            <Grid item className={classes.actionButton} xs={6}>
              <CustomButton
                onClick={() => {
                  if (!mwState.isSaveInProgress) {
                    setMwState((p) => ({
                      ...p,
                      isSaveInProgress: true,
                    }));
                    switch (mwState.moduleName) {
                      case "address_book":
                        addressBookSaveAndBack();
                        break;
                      case "Client":
                        customerSaveAndBack();
                        break;
                      default:
                        saveAndBackToList();
                    }
                  }
                }}
                size="medium"
                color="action"
                id="btnAdd"
                variant="contained"
                type="submit"
                disabled={
                  !mwState.moduleFormHasChanges ||
                  (mwState.moduleName === "AR" &&
                    moduleItemData.alerts?.length === 0) ||
                  mwState.isSaveInProgress ||
                  Object.keys(formik.errors)?.length > 0
                }
                startIcon={<CheckIcon />}
              >
                {mwState.isSaveInProgress
                  ? Language.translate("Saving")
                  : btnSaveText}
              </CustomButton>
              <span className={classes.gapSpan} />
              {mwState.moduleName === "Admin Device" && (
                <CustomButton
                  onClick={() => {
                    if (!mwState.isSaveInProgress) {
                      setMwState((p) => ({
                        ...p,
                        isSaveInProgress: true,
                      }));
                      adminDeviceSaveAndAdd();
                    }
                  }}
                  size="medium"
                  color="action"
                  id="btnSaveAndAdd"
                  variant="contained"
                  type="submit"
                  disabled={
                    !mwState.moduleFormHasChanges ||
                    mwState.isSaveInProgress ||
                    Object.keys(formik.errors)?.length > 0
                  }
                  startIcon={<AddIcon />}
                >
                  {btnSaveAndAddText}
                </CustomButton>
              )}
            </Grid>
            <Grid item className="cancelButton" align="right" xs={6}>
              <CustomButton
                onClick={
                  !mwState.moduleFormHasChanges
                    ? cancelAndBackToListingFunction
                    : handleDialogOpen
                }
                size="medium"
                color="cancel"
                id="btnCancel"
                variant="contained"
                startIcon={<CloseIcon />}
                disabled={mwState.isSaveInProgress}
              >
                {btnCancelText}
              </CustomButton>
              <span className={classes.gapSpan} />
              {mwState.verifyDeletePermission &&
              moduleItemData.id &&
              !["Device", "Map", "Tak"].includes(mwState.moduleName) ? (
                <CustomButton
                  onClick={deleteItem}
                  size="medium"
                  color="delete"
                  id="btnDelete"
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  disabled={mwState.isSaveInProgress}
                >
                  {btnDeleteText}
                </CustomButton>
              ) : null}
            </Grid>
          </Grid>
        );
      case MW_SINGLEPAGE_MODE: //currently being used by Profile & Fed
        return (
          <Grid
            justifyContent="flex-start"
            className={classes.footer}
            container
          >
            <Grid item className={classes.actionButton} xs={6}>
              <CustomButton
                onClick={() => {
                  if (!mwState.isSaveInProgress) {
                    setMwState((p) => ({
                      ...p,
                      isSaveInProgress: true,
                    }));
                    saveAndClose();
                  }
                }}
                size="medium"
                color="action"
                id="btnAdd"
                variant="contained"
                type="submit"
                disabled={
                  !mwState.moduleFormHasChanges ||
                  mwState.isSaveInProgress ||
                  Object.keys(formik.errors)?.length > 0
                }
                startIcon={<CheckIcon />}
              >
                {mwState.isSaveInProgress
                  ? Language.translate("Saving")
                  : btnSaveText}
              </CustomButton>
            </Grid>
            <Grid item className="cancelButton" align="right" xs={6}>
              <CustomButton
                onClick={
                  !mwState.moduleFormHasChanges
                    ? mwState.closeModuleWrapperPopper
                    : handleDialogOpenClose
                }
                size="medium"
                color="cancel"
                id="btnCancel"
                variant="contained"
                startIcon={<CloseIcon />}
                disabled={mwState.isSaveInProgress}
              >
                {btnCancelText}
              </CustomButton>
            </Grid>
          </Grid>
        );
      case MW_ALERTS_MODULE_MODE: //currently being used by Alerts
        const confirmationText =
          getAcknowledgeAlertsIds(mwState.filteredModuleData).length > 0
            ? getAcknowledgeAlertsIds(mwState.filteredModuleData).length + " "
            : "";
        return (
          <>
            <CustomDialogYesNo
              open={alertsConfirmationOpen}
              onNoAction={() => setAlertsConfirmationOpen(false)}
              onYesAction={() =>
                acknowledgeAlerts(
                  getAcknowledgeAlertsIds(mwState.filteredModuleData)
                )
              }
              text={
                acknowledgeAllText +
                " " +
                confirmationText +
                Language.translate("alerts") +
                "?"
              }
            />

            <Grid
              justifyContent="flex-start"
              className={classes.footer}
              container
            >
              <Grid item className={classes.actionButton} xs={3}>
                {mwState.filteredModuleData &&
                Object.values(mwState.filteredModuleData)?.length > 0 ? (
                  <CustomButton
                    onClick={() => setAlertsConfirmationOpen(true)}
                    size="medium"
                    color="command"
                    id="btnAdd"
                    variant="contained"
                    type="submit"
                    startIcon={<CheckIcon />}
                    disabled={
                      getAcknowledgeAlertsIds(mwState.filteredModuleData)
                        .length < 1
                    }
                  >
                    {Language.translate("Acknowledge All").toUpperCase()}
                  </CustomButton>
                ) : (
                  <div />
                )}
              </Grid>
              {mwState.showPagination &&
                mwState.moduleData &&
                Object.values(mwState.moduleData)?.length > 0 && (
                  <Grid item align="right" className="pagination" xs={9}>
                    <CustomPagination
                      pagination={mwState.pagination}
                      onPageChange={onPageChange}
                    />
                  </Grid>
                )}
            </Grid>
          </>
        );
      case MW_ASSETS_MODULE_MODE: //used by Assets
        // return <CustomAssetSelectorContextProvider>
        return (
          <Grid
            justifyContent="space-between"
            className={classes.footer}
            container
          >
            <Grid item className={classes.actionButton} xs={3}>
              {mwState.verifyAddPermission !== 0 ? (
                <CustomButton
                  onClick={(e) => changeToAddModeFromAssets(e, 1)}
                  size="medium"
                  color="command"
                  id="btnAdd"
                  variant="contained"
                  startIcon={<KeyboardArrowRightIcon />}
                >
                  {btnAddText}
                </CustomButton>
              ) : null}
            </Grid>
            <Grid item align="right" className="generateFile" xs={9}>
              <MasterDataFile
                label={Language.translate("Master Data File").toUpperCase()}
                mainGroupName={mwState.mainGroupName}
              />
            </Grid>
          </Grid>
        );
      // </CustomAssetSelectorContextProvider>;
      case MW_SYNC_MODULE_MODE: //used by Sync
        return (
          <Grid
            justifyContent="flex-start"
            className={classes.footer}
            container
          >
            <Grid item container className={classes.syncActionButton} xs={5}>
              {SyncMenu.getCountOfStatus(Sync.get()).synced_count > 0 && (
                <Grid item xs={4} className={classes.syncFooterItem}>
                  <CustomTooltip
                    title={Language.translate("Up-to-date").toUpperCase()}
                    placement="top"
                  >
                    <span className={classes.syncFooterText}>
                      {Language.translate("Up-to-date").toUpperCase()}
                    </span>
                  </CustomTooltip>
                  <Badge
                    badgeContent={
                      SyncMenu.getCountOfStatus(Sync.get()).synced_count
                    }
                    color="primary"
                    className={classes.syncBadge}
                  />
                </Grid>
              )}
              {SyncMenu.getCountOfStatus(Sync.get()).pending_count > 0 && (
                <Grid item xs={4} className={classes.pendingFooterItem}>
                  <CustomTooltip
                    title={Language.translate("Pending").toUpperCase()}
                    placement="top"
                  >
                    <span className={classes.syncFooterText}>
                      {Language.translate("Pending").toUpperCase()}
                    </span>
                  </CustomTooltip>
                  <Badge
                    badgeContent={
                      SyncMenu.getCountOfStatus(Sync.get()).pending_count
                    }
                    color="primary"
                    className={classes.pendingBadge}
                  />
                </Grid>
              )}
              {SyncMenu.getCountOfStatus(Sync.get()).syncing_count > 0 && (
                <Grid item xs={4} className={classes.syncingFooterItem}>
                  <span className={classes.sycnIcon}>
                    <SyncIcon />
                  </span>
                  <CustomTooltip
                    title={Language.translate("Syncing").toUpperCase()}
                    placement="top"
                  >
                    <span className={classes.syncFooterText}>
                      {Language.translate("Syncing").toUpperCase()}
                    </span>
                  </CustomTooltip>
                  <Badge
                    badgeContent={
                      SyncMenu.getCountOfStatus(Sync.get()).syncing_count
                    }
                    color="primary"
                    className={classes.syncingBadge}
                  />
                </Grid>
              )}
            </Grid>
            {mwState.showPagination && (
              <Grid item align="right" className="pagination" xs={7}>
                <CustomPagination
                  pagination={mwState.pagination}
                  onPageChange={onPageChange}
                />
              </Grid>
            )}
          </Grid>
        );
      case MW_MESSAGE_MODULE_MODE: //being used by Message
        return (
          <Grid
            justifyContent="space-between"
            className={classes.footer}
            container
          >
            {mwState.verifyAddPermission &&
              mwState.wrapperDisplaySubMode !==
                MW_MESSAGE_MODULE_COMPOSE_MODE && (
                <Grid item className={classes.actionButton} xs={3}>
                  <CustomButton
                    onClick={changeToComposeMode}
                    size="medium"
                    color="command"
                    id="btnCompose"
                    variant="contained"
                    startIcon={<NearMeIcon />}
                  >
                    {Language.translate("Compose")}
                  </CustomButton>
                </Grid>
              )}
            {mwState.wrapperDisplaySubMode === MW_MESSAGE_MODULE_LIST_MODE &&
              !mwState.reloadMessages &&
              !mwState.moduleData.reloading &&
              mwState.showPagination && (
                <Grid item align="right" className="pagination" xs={9}>
                  <CustomPagination
                    pagination={mwState.pagination}
                    onPageChange={onMessagingPageChange}
                  />
                </Grid>
              )}
            {mwState.wrapperDisplaySubMode ===
              MW_MESSAGE_MODULE_COMPOSE_MODE && (
              <Grid container>
                <Grid item className={classes.actionButton} xs={6}>
                  <CustomButton
                    onClick={sendMessage}
                    size="medium"
                    color="action"
                    id="btnAdd"
                    variant="contained"
                    type="submit"
                    disabled={
                      (moduleItemData.recipients &&
                        _.isEmpty(moduleItemData.recipients.users) &&
                        _.isEmpty(moduleItemData.recipients.groups) &&
                        _.isEmpty(moduleItemData.recipients.devices)) ||
                      Object.keys(formik.errors)?.length > 0
                        ? true
                        : !mwState.moduleFormHasChanges
                    }
                    startIcon={<NearMeIcon />}
                  >
                    {Language.translate("Send")}
                  </CustomButton>
                </Grid>
                <Grid item className="cancelButton" align="right" xs={6}>
                  <CustomButton
                    onClick={
                      !mwState.moduleFormHasChanges
                        ? mwState.cancelAndBackToMessageListingMode
                        : handleDialogOpenMessage
                    }
                    size="medium"
                    color="cancel"
                    id="btnCancel"
                    variant="contained"
                    startIcon={<CloseIcon />}
                  >
                    {btnCancelText}
                  </CustomButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        );
      case MW_IRIDIUM_MODULE_MODE: //currently being used by Iridium Billing
        return (
          <Grid
            justifyContent="flex-start"
            className={classes.footer}
            container
          >
            <Grid item className={classes.actionButton} xs={3}>
              <CustomButton
                onClick={handleExport}
                size="medium"
                color="command"
                id="btnAdd"
                variant="contained"
                type="submit"
              >
                {Language.translate("EXPORT AS CSV")}
              </CustomButton>
            </Grid>
          </Grid>
        );
      default:
        break;
    }
  } else {
    return null;
  }
}

export default ModuleWrapperFooter;
